/**
 * /pourquoi-consulter — Landing hub. Les sous-pages par thème seront ajoutées
 * progressivement, uniquement lorsqu'elles disposeront d'un contenu de qualité.
 */
import React from "react";
import { Seo } from "../components/Layout";
import { PageHero, ProseSection, EndCTA } from "../components/PageParts";
import { Link } from "react-router-dom";

const THEMES = [
  "Stress et surcharge mentale",
  "Anxiété, angoisse, ruminations",
  "Sommeil difficile, endormissement",
  "Confiance en soi, estime, prise de parole",
  "Difficultés relationnelles",
  "Schémas qui se répètent",
  "Transitions de vie (professionnelles, personnelles)",
  "Blocages émotionnels liés à un événement",
  "Perte de sens, questionnement existentiel",
];

export default function PourquoiConsulter() {
  const BC = [{ to: "/", label: "Accueil" }, { label: "Pourquoi consulter\u00A0?" }];
  return (
    <>
      <Seo
        title="Pourquoi consulter en hypnose ?"
        description="Stress, anxiété, sommeil, confiance en soi, transitions de vie, schémas répétitifs… les motifs de consultation les plus fréquents en hypnothérapie. Cabinet à Pantin ou séance en ligne."
        canonical="https://www.parfaitsimparfaits.fr/pourquoi-consulter"
      />

      <PageHero
        breadcrumb={BC}
        eyebrow="Pourquoi consulter"
        title="Ce qui amène"
        italic="à consulter en hypnose."
        subtitle="Chaque motif est légitime. Voici les situations les plus fréquentes — sans hiérarchie, ni jugement."
      />

      <ProseSection>
        <p>
          On associe encore souvent l'hypnose à quelques situations spectaculaires — la douleur, les
          phobies. En réalité, la <strong className="text-sand-900 font-medium">grande
          majorité de mes consultations</strong> concernent des thèmes plus quotidiens et souvent
          plus intimes&nbsp;: un stress qui devient envahissant, un sommeil qui se dégrade, une confiance
          en soi qui s'effrite, une relation qui se rejoue toujours dans les mêmes impasses, ou un
          sentiment diffus de ne plus être vraiment à sa place.
        </p>
        <p>
          Il n'y a <strong className="text-sand-900 font-medium">pas de bon ou de mauvais motif</strong>&nbsp;:
          si un thème revient et vous coûte, c'est déjà une raison suffisante pour en parler.
        </p>

        <h2 className="font-serif text-3xl text-sand-900">Motifs fréquemment rencontrés</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 list-disc pl-5 marker:text-[#B07A45]">
          {THEMES.map((t) => (<li key={t}>{t}</li>))}
        </ul>

        <h2 className="font-serif text-3xl text-sand-900">Quelle approche pour quel motif&nbsp;?</h2>
        <p>
          En fonction de votre situation, je vous oriente vers l'une des deux approches proposées&nbsp;:
        </p>
        <p>
          → Pour un <strong className="text-sand-900 font-medium">objectif précis</strong> à court/moyen
          terme (stress, sommeil, confiance, blocage émotionnel), la{" "}
          <Link to="/hypnose/ericksonienne" className="text-[#B07A45] underline hover:text-sand-900">
            séance d'hypnose ericksonienne
          </Link>{" "}
          est généralement la plus adaptée.
        </p>
        <p>
          → Pour une <strong className="text-sand-900 font-medium">exploration plus large</strong> — sens,
          cohérence, transition intérieure, schémas qui reviennent —, la{" "}
          <Link to="/hypnose/spirituelle" className="text-[#B07A45] underline hover:text-sand-900">
            séance d'hypnose spirituelle
          </Link>{" "}
          sera plus juste.
        </p>
        <p>
          En cas de doute, nous en parlons ensemble avant de fixer un rendez-vous. Il n'y a jamais
          d'obligation d'engagement.
        </p>
      </ProseSection>

      <EndCTA
        title="Parlons de ce qui vous amène."
        text="Un premier échange téléphonique gratuit permet souvent d'y voir plus clair."
        testid="pc-end-cta"
      />
    </>
  );
}
